import { Element, Point } from '@/types/elements';

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function getElementBounds(element: Element): Bounds | null {
  if (!element) {
    console.warn('[getElementBounds] Element is undefined/null');
    return null;
  }
  switch (element.type) {
    case 'rectangle':
    case 'diamond':
    case 'icon':
    case 'svg':
    case 'image':
      return {
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
      };

    case 'ellipse':
      return {
        x: element.x,
        y: element.y,
        width: element.radiusX * 2,
        height: element.radiusY * 2,
      };

    case 'line': {
      const lineElement = element as any;
      let points: Point[] = [];

      if (lineElement.points && lineElement.points.length > 0) {
        points = lineElement.points;
      } else if (lineElement.startPoint && lineElement.endPoint) {
        points = [lineElement.startPoint, lineElement.endPoint];
      } else {
        console.warn('[getElementBounds] Line element has no valid points');
        return null;
      }

      const xs = points.map(p => p.x);
      const ys = points.map(p => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      return {
        x: minX,
        y: minY,
        width: Math.max(1, maxX - minX), // Ensure minimum width
        height: Math.max(1, maxY - minY), // Ensure minimum height
      };
    }

    case 'arrow': {
      const arrowElement = element as any;
      let points: Point[] = [];

      if (arrowElement.points && arrowElement.points.length > 0) {
        points = arrowElement.points;
      } else if (arrowElement.startPoint && arrowElement.endPoint) {
        points = [arrowElement.startPoint, arrowElement.endPoint];
      } else {
        console.warn('[getElementBounds] Arrow element has no valid points');
        return null;
      }

      const xs = points.map(p => p.x);
      const ys = points.map(p => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      return {
        x: minX,
        y: minY,
        width: Math.max(1, maxX - minX), // Ensure minimum width
        height: Math.max(1, maxY - minY), // Ensure minimum height
      };
    }

    case 'text':
      return {
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
      };

    case 'group':
      return {
        x: element.x,
        y: element.y,
        width: (element as any).width,
        height: (element as any).height,
      };

    default:
      return { x: 0, y: 0, width: 0, height: 0 };
  }
}

export function getCombinedBounds(elements: Element[]): Bounds | null {
  if (elements.length === 0) return null;

  const bounds = elements
    .map(getElementBounds)
    .filter((b): b is Bounds => b !== null);

  if (bounds.length === 0) return null;

  const minX = Math.min(...bounds.map(b => b.x));
  const minY = Math.min(...bounds.map(b => b.y));
  const maxX = Math.max(...bounds.map(b => b.x + b.width));
  const maxY = Math.max(...bounds.map(b => b.y + b.height));

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
